import { Case, CaseDuplicateType, CaseStatus, OrganType } from '@/api/apis/case';
import { PaginatedBody } from '@/api/types';
import { queryClient } from '@/queries';
import { caseQueryKeys } from '@/queries/case/case.keys';
import { getCachedCaseListItems } from './case-cache.utils';

export const refetchDuplicatesIfCaseClosed = (caseStatus: CaseStatus) => {
  if (caseStatus === CaseStatus.Closed) {
    queryClient.invalidateQueries({ queryKey: caseQueryKeys.getDuplicates(), exact: false });
  }
};

// Cases in a terminal state are never duplication candidates.
const TERMINAL_DUPLICATE_STATUSES: CaseStatus[] = [CaseStatus.Deleted, CaseStatus.Finished, CaseStatus.Closed];
const isDuplicateCandidate = (status: CaseStatus) => !TERMINAL_DUPLICATE_STATUSES.includes(status);

// Cases are duplicates when they share donor + organType.
const duplicateGroupKey = (c: { donorId?: string; organType: OrganType }) =>
  `${c.donorId?.toUpperCase()}|${c.organType}`;

const toDuplicate = (c: Case): CaseDuplicateType => ({
  caseId: c.caseId,
  caseNumber: null,
  donorId: c.donorId,
  organType: c.organType,
  caseName: c.caseName ?? '',
  status: c.status,
});

// Optimistically resync the global duplicates list after a case's donorId or organType changed:
// move the case into its new group, pull in any partners, and drop groups that no longer have two cases.
export const updateCaseDuplicatesCache = (data: Case) => {
  const knownCases = getCachedCaseListItems();

  // Partners of the case's new group, pulled from board lists so a previously-unique one is added now.
  const newGroupMembers = knownCases
    .filter((c) => isDuplicateCandidate(c.status) && duplicateGroupKey(c) === duplicateGroupKey(data))
    .map(toDuplicate);

  queryClient.setQueriesData<PaginatedBody<CaseDuplicateType>>({ queryKey: caseQueryKeys.getDuplicates() }, (old) => {
    if (!old) return old;

    const reflowed = old.items.map((c) =>
      c.caseId === data.caseId ? { ...c, donorId: data.donorId, organType: data.organType, status: data.status } : c,
    );

    // Reflowed items win over newGroupMembers so the real caseNumber is preserved.
    const byId = new Map<string, CaseDuplicateType>();
    [...newGroupMembers, ...reflowed].forEach((c) => byId.set(c.caseId, c));
    const candidates = [...byId.values()].filter((c) => isDuplicateCandidate(c.status));

    const groupSize = new Map<string, number>();
    candidates.forEach((c) => groupSize.set(duplicateGroupKey(c), (groupSize.get(duplicateGroupKey(c)) ?? 0) + 1));

    return { ...old, items: candidates.filter((c) => (groupSize.get(duplicateGroupKey(c)) ?? 0) > 1) };
  });

  queryClient.invalidateQueries({ queryKey: caseQueryKeys.getDuplicates(), exact: false });
};
